<template>
  <div class="agenda-page">
    <!-- Editorial Hero -->
    <section class="ahrs-page-hero hero-photo-agenda">
      <div class="ahrs-page-hero-wrap">
        <div class="ahrs-page-eyebrow" data-aos="fade-up">
          <span class="dot"></span>Summit Programme
        </div>
        <h1 class="ahrs-page-title" data-aos="fade-up" data-aos-delay="100">
          Four days of
          <span class="ahrs-page-italic">focused dialogue.</span>
        </h1>
        <p class="ahrs-page-lede" data-aos="fade-up" data-aos-delay="200">
          Keynotes, panels, masterclasses, and networking sessions &mdash;
          shaping Africa&rsquo;s workforce for the future.
        </p>
        <div class="ahrs-page-meta" data-aos="fade-up" data-aos-delay="300">
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <rect x="3" y="4.5" width="18" height="17" rx="2" />
              <path d="M3 9h18M8 2.5v4M16 2.5v4" />
            </svg>
            18&ndash;22 August 2026
          </div>
          <div class="m">
            <svg viewBox="0 0 24 24" stroke-linecap="round">
              <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" />
              <circle cx="12" cy="10" r="2.6" />
            </svg>
            Mount Meru Hotel, Arusha
          </div>
        </div>
        <div class="ahrs-page-stats" data-aos="fade-up" data-aos-delay="400">
          <div class="ahrs-page-stat">
            <span class="n">5</span>
            <span class="l">Days</span>
          </div>
          <div class="ahrs-page-stat">
            <span class="n">8</span>
            <span class="l">Sessions</span>
          </div>
          <div class="ahrs-page-stat">
            <span class="n">10+</span>
            <span class="l">Speakers</span>
          </div>
          <div class="ahrs-page-stat">
            <span class="n">4</span>
            <span class="l">Masterclasses</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Agenda Content -->
    <section class="agenda-content">
      <div class="container-wide">

        <!-- Day Tabs -->
        <div class="day-tabs" data-aos="fade-up">
          <button
            v-for="(day, index) in agendaDays"
            :key="index"
            :class="['day-tab', { active: selectedDay === index }]"
            @click="selectedDay = index"
          >
            <span class="tab-date">{{ day.shortDate }}</span>
            <span class="tab-title">{{ day.title }}</span>
          </button>
        </div>

        <!-- Day Header -->
        <div class="day-header-card" data-aos="fade-up">
          <div class="day-num">{{ String(selectedDay + 1).padStart(2, '0') }}</div>
          <div class="day-info">
            <h2 class="day-name">{{ currentDay.title }}</h2>
            <p class="day-date"><i class="fas fa-calendar-alt"></i> {{ currentDay.date }}</p>
          </div>
        </div>

        <!-- Sessions List -->
        <transition name="fade-slide" mode="out-in">
          <div :key="selectedDay" class="sessions-list">
            <div
              v-for="session in currentDay.sessions"
              :key="session.id"
              :class="['session-card', `type-${session.type}`]"
              data-aos="fade-up"
            >
              <div class="session-time">
                <i class="fas fa-clock"></i>
                {{ session.time }}
              </div>
              <div class="session-body">
                <h3 class="session-title">{{ session.title }}</h3>
                <div v-if="session.participants" class="session-participants">
                  <span class="sp-label"><i class="fas fa-user-group"></i> Participants</span>
                  <span class="sp-value">{{ session.participants }}</span>
                </div>
                <p v-if="session.subtitle" class="session-subtitle">{{ session.subtitle }}</p>
                <p v-if="session.description" class="session-desc">{{ session.description }}</p>
                <ol v-if="session.items && session.items.length" class="session-items">
                  <li v-for="item in session.items" :key="item">{{ item }}</li>
                </ol>
              </div>
            </div>
          </div>
        </transition>

        <!-- CTA -->
        <div class="agenda-cta" data-aos="zoom-in">
          <div class="cta-glow"></div>
          <h2 class="cta-title">Don't miss a single session</h2>
          <p class="cta-subtitle">Register now to secure your spot at AHRS 2026 and be part of Africa's HR transformation.</p>
          <router-link to="/registration" class="cta-btn">
            <i class="fas fa-ticket-alt"></i> Register Now
          </router-link>
        </div>

      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "AgendaView",
  data() {
    return {
      selectedDay: 0,
      agendaDays: [
        {
          shortDate: "18 Aug",
          title: "Registration",
          date: "18 August 2026 (Tuesday)",
          theme: "Arrival Day",
          sessions: [
            { id: 1, time: "08:00 – 18:00", title: "Arrival and Registration", subtitle: "Reception Desk", description: "Collection of your ID Badge and Delegate Package.", location: "Reception Desk", sessionType: "Registration", type: "registration" },
          ],
        },
        {
          shortDate: "19 Aug",
          title: "Opening Ceremony",
          date: "19 August 2026 (Wednesday)",
          theme: "Official Opening & Keynote Sessions",
          sessions: [
            { id: 1, time: "07:00 – 09:00", title: "Registration & Coffee", description: "", location: "Main Foyer", sessionType: "Registration", type: "registration" },
            { id: 2, time: "09:00 – 09:10", title: "Arrival of the Guest of Honor", description: "", location: "Main Entrance", sessionType: "Protocol", type: "featured" },
            { id: 3, time: "09:10 – 09:20", title: "Visiting Exhibition Area", description: "", location: "Exhibition Hall", sessionType: "Exhibition", type: "session" },
            { id: 4, time: "09:20 – 09:25", title: "Guest of Honor Entering the Main Hall", description: "", location: "Main Auditorium", sessionType: "Protocol", type: "featured" },
            { id: 5, time: "09:25 – 09:30", title: "Anthems", description: "", location: "Main Auditorium", sessionType: "Ceremony", type: "session" },
            { id: 6, time: "09:30 – 09:35", title: "Prayers", description: "", location: "Main Auditorium", sessionType: "Ceremony", type: "session" },
            { id: 7, time: "09:35 – 10:00", title: "Welcome and Introduction", description: "", location: "Main Auditorium", sessionType: "Welcome", type: "session" },
            { id: 8, time: "10:00 – 11:00", title: "Opening Remarks", description: "", location: "Main Auditorium", sessionType: "Opening", type: "featured" },
            { id: 9, time: "11:00 – 12:00", title: "Speech", description: "", location: "Main Auditorium", sessionType: "Keynote Speech", type: "keynote" },
            { id: 10, time: "12:00 – 12:30", title: "Group Photo", description: "", location: "Main Auditorium", sessionType: "Ceremony", type: "session" },
            { id: 11, time: "12:30 – 13:00", title: "Departure of the Guest of Honor", description: "", location: "Main Entrance", sessionType: "Protocol", type: "session" },
            { id: 12, time: "13:00 – 14:00", title: "Lunch & Exhibition", description: "", location: "Exhibition Hall & Dining Area", sessionType: "Lunch & Expo", type: "lunch" },
            { id: 13, time: "14:00 – 14:30", title: "Networking & Entertainment", description: "", location: "Main Hall", sessionType: "Networking", type: "break" },
            { id: 14, time: "14:30 – 15:00", title: "Keynote Address:", subtitle: "Workforce Strategies in Today's Organizations: Opportunities and Challenges", description: "", location: "Main Auditorium", sessionType: "Keynote", type: "keynote" },
            { id: 15, time: "15:00 – 15:30", title: "Partner Spotlight", description: "", location: "Main Auditorium", sessionType: "Partner Spotlight", type: "session" },
            { id: 16, time: "15:30 – 16:30", title: "Panel Discussion:", subtitle: "Africa's Gig and Remote Workforce: Policy and Productivity Strategies", description: "", location: "Main Auditorium", sessionType: "Panel Discussion", type: "panel" },
            { id: 17, time: "16:30 – 17:30", title: "Partner Spotlight", description: "", location: "Main Auditorium", sessionType: "Partner Spotlight", type: "session" },
            { id: 18, time: "17:30 – 18:00", title: "Evening Tea & Networking", description: "", location: "Main Foyer", sessionType: "Networking", type: "break" },
          ],
        },
        {
          shortDate: "Roundtable",
          title: "Leadership Roundtable",
          date: "19 August 2026 (Wednesday)",
          theme: "Heads of National Associations & Boards",
          sessions: [
            { id: 19, time: "16:00 – 18:00", title: "Leadership Roundtable", participants: "Heads of HR National Associations & Boards", subtitle: "Agenda", description: "", items: ["Opening Meeting", "Introduction & Welcome address", "Matters Arising in Africa's HR Landscape", "Any Other Business", "Closing Meeting"], location: "Roundtable Room", sessionType: "Roundtable", type: "featured" },
          ],
        },
        {
          shortDate: "20 Aug",
          title: "Sessions",
          date: "20 August 2026 (Thursday)",
          theme: "Human Capital, Leadership & Workforce Resilience",
          sessions: [
            { id: 17, time: "08:00 – 09:00", title: "Registration", description: "", location: "Main Foyer", sessionType: "Registration", type: "registration" },
            { id: 18, time: "08:30 – 09:00", title: "Opening Sessions", description: "", location: "Main Auditorium", sessionType: "Opening", type: "opening" },
            { id: 19, time: "09:00 – 09:30", title: "Keynote Address:", subtitle: "Bridging the Talent Gap: A Global Employment Paradigm", description: "", location: "Main Auditorium", sessionType: "Keynote", type: "keynote" },
            { id: 20, time: "09:30 – 09:45", title: "Open Dialogue (Q&A)", description: "", location: "Main Auditorium", sessionType: "Q&A", type: "session" },
            { id: 39, time: "09:45 – 10:00", title: "Partner Spotlight", description: "", location: "Exhibition Hall", sessionType: "Partner Spotlight", type: "session" },
            { id: 21, time: "10:00 – 11:00", title: "Coffee Break & Networking", description: "", location: "Main Foyer", sessionType: "Break", type: "break" },
            { id: 22, time: "11:00 – 11:40", title: "Keynote Address:", subtitle: "The Strategic Role of Human Capital in Driving Africa's Transformation", description: "", location: "Main Auditorium", sessionType: "Keynote", type: "keynote" },
            { id: 23, time: "11:40 – 12:40", title: "Panel Discussion:", subtitle: "Building Transformational Workplace Cultures in Africa's Digital Revolution", description: "", location: "Main Auditorium", sessionType: "Panel Discussion", type: "panel" },
            { id: 24, time: "12:40 – 13:00", title: "Partner Spotlight", description: "", location: "Exhibition Hall", sessionType: "Partner Spotlight", type: "session" },
            { id: 25, time: "13:00 – 14:00", title: "Lunch & Exhibition", description: "", location: "Exhibition Hall & Dining Area", sessionType: "Lunch & Expo", type: "lunch" },
            { id: 26, time: "14:00 – 14:40", title: "Keynote Address:", subtitle: "Building Resilient Human Capital in an Era of Economic Fragmentation", description: "", location: "Main Auditorium", sessionType: "Keynote", type: "keynote" },
            { id: 27, time: "14:40 – 15:40", title: "Panel Discussion:", subtitle: "Modernizing Labour Standards for the Emerging Workforce and Digital Economy", description: "", location: "Main Auditorium", sessionType: "Panel Discussion", type: "panel" },
            { id: 28, time: "15:40 – 16:00", title: "Partner Spotlight", description: "", location: "Exhibition Hall", sessionType: "Partner Spotlight", type: "session" },
            { id: 29, time: "16:00 – 17:00", title: "Panel Discussion:", subtitle: "Strategic Human-Centered Leadership in the Age of AI and Transformation", description: "", location: "Main Auditorium", sessionType: "Panel Discussion", type: "panel" },
            { id: 30, time: "17:00 – 17:30", title: "Evening Tea & Networking", description: "", location: "Main Foyer", sessionType: "Networking", type: "break" },
            { id: 31, time: "17:30 – 18:00", title: "Closing Session", description: "", location: "Main Auditorium", sessionType: "Closing", type: "closing" },
          ],
        },
        {
          shortDate: "Party",
          title: "Cocktail Party",
          date: "20 August 2026 (Thursday)",
          theme: "Networking & Entertainment",
          sessions: [
            { id: 32, time: "18:00 – 23:00", title: "Networking & Entertainment", participants: "MC & All Delegates", description: "", location: "Main Hall", sessionType: "Cocktail Party", type: "featured" },
          ],
        },
        {
          shortDate: "21 Aug",
          title: "Masterclass",
          date: "21 August 2026 (Friday)",
          theme: "AI Ethics, Data Protection & Strategic Leadership",
          sessions: [
            { id: 32, time: "08:00 – 08:30", title: "Registration & Opening Session", description: "", location: "Main Foyer", sessionType: "Registration", type: "registration" },
            { id: 33, time: "08:30 – 10:30", title: "Masterclass:", description: "", items: ["Workforce Transformation in the Digital Age: Balancing Technology, Culture & People", "Mastering Human Capital Risk for High-Performance Project Delivery"], location: "Masterclass Hall", sessionType: "Masterclass", type: "masterclass" },
            { id: 34, time: "10:30 – 11:30", title: "Coffee Break", description: "", location: "Main Foyer", sessionType: "Break", type: "break" },
            { id: 35, time: "11:30 – 13:30", title: "Masterclass:", description: "", items: ["Mastering Ethics, Governance and Compliance in the Modern Workplace", "Strategic Leadership for Driving Digital Transformation in Organizations"], location: "Masterclass Hall", sessionType: "Masterclass", type: "masterclass" },
            { id: 36, time: "13:30 – 14:30", title: "Lunch & Networking", description: "", location: "Dining Area", sessionType: "Lunch", type: "lunch" },
            { id: 37, time: "14:30 – 15:00", title: "Closing Session", description: "", location: "Main Auditorium", sessionType: "Closing", type: "closing" },
          ],
        },
        /* Guests & Topics tab — hidden for now (uncomment to restore)
        {
          shortDate: "Featured",
          title: "Guests & Topics",
          date: "Keynote Addresses & Masterclasses",
          theme: "Guest Speakers and Their Topics",
          sessions: [
            { id: 39, time: "Keynote", title: "Peck Kem Low", subtitle: "Workforce Strategies in Today's Organizations: Opportunities and Challenges", description: "President, World Federation of People Management Associations (WFPMA)", location: "", sessionType: "Keynote", type: "keynote" },
            { id: 40, time: "Keynote", title: "Bettina Schaller", subtitle: "Bridging the Talent Gap: A Global Employment Paradigm", description: "President, World Employment Confederation (WEC)", location: "", sessionType: "Keynote", type: "keynote" },
            { id: 41, time: "Address", title: "Dr. Hanan Morsy", subtitle: "The Role of Human Capital in Driving Africa's Economic Transformation", description: "Deputy Executive Secretary & Chief Economist, UNECA", location: "", sessionType: "Address", type: "featured" },
            { id: 42, time: "Masterclass", title: "Zara Marfo", subtitle: "Workforce Transformation in the Digital Age: Balancing Technology, Culture & People", description: "Director (DEIS&SR), Adecco Group", location: "", sessionType: "Masterclass", type: "masterclass" },
            { id: 43, time: "Masterclass", title: "Dr. Jackson Sawe", subtitle: "Mastering Human Capital Risk for High-Performance Project Delivery", description: "Vice Chairperson, TPPA", location: "", sessionType: "Masterclass", type: "masterclass" },
            { id: 44, time: "Masterclass", title: "Prof. John Malepo", subtitle: "Mastering Ethics, Governance and Compliance in the Modern Workplace", description: "Executive Director, SAAPAM", location: "", sessionType: "Masterclass", type: "masterclass" },
            { id: 45, time: "Masterclass", title: "Olusegun Mojeed", subtitle: "Strategic Leadership for Driving Digital Transformation in Organizations", description: "Vice President, AHRC", location: "", sessionType: "Masterclass", type: "masterclass" },
          ],
        },
        */
        {
          shortDate: "22 Aug",
          title: "Heritage Tour",
          date: "22 August 2026 (Saturday)",
          theme: "Tarangire National Park — Tanzania's Natural Heritage",
          sessions: [
            { id: 38, time: "06:00 – 18:00", title: "Tarangire National Park", description: "", location: "Tarangire National Park, Tanzania", sessionType: "Heritage Tour", type: "tour" },
          ],
        },
      ],
    };
  },
  computed: {
    currentDay() {
      return this.agendaDays[this.selectedDay];
    },
  },
  mounted() {
    this.initParticles();
  },
  beforeUnmount() {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    if (this._resizeHandler) window.removeEventListener("resize", this._resizeHandler);
  },
  methods: {
    onHeroMouseMove(e) {
      const glow = this.$refs.cursorGlow;
      if (!glow) return;
      const rect = this.$refs.heroSection.getBoundingClientRect();
      glow.style.left = e.clientX - rect.left + "px";
      glow.style.top = e.clientY - rect.top + "px";
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const resize = () => {
        const page = document.querySelector(".agenda-page");
        canvas.width = window.innerWidth;
        canvas.height = page ? page.offsetHeight : window.innerHeight;
      };
      resize();
      this._resizeHandler = resize;
      window.addEventListener("resize", resize);
      const colors = ["#4ade80", "#22c55e", "#f59e0b", "#10b981", "#86efac"];
      const particles = Array.from({ length: 60 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2.5 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.6 + 0.2,
      }));
      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        particles.forEach((p) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = p.color + Math.floor(p.alpha * 255).toString(16).padStart(2, "0");
          ctx.fill();
          p.x += p.vx;
          p.y += p.vy;
          if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
          if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        });
        this.animationFrame = requestAnimationFrame(draw);
      };
      draw();
    },
  },
};
</script>

<style scoped>
/* ── Page ───────────────────────────────────────────── */
.agenda-page {
  min-height: 100vh;
  background: #f8faf9;
  position: relative;
  overflow: hidden;
}

/* Editorial hero backdrop photo */
.hero-photo-agenda {
  --hero-photo: url("~@/assets/images/past Ahrs/round table.jpg");
}

/* ── Animated Background ────────────────────────────── */
.animated-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}
.particle-canvas { position: absolute; inset: 0; opacity: 0.7; }
.bg-grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(74, 222, 128, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74, 222, 128, 0.1) 1px, transparent 1px);
  background-size: 60px 60px;
}
.gradient-orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.3; }
.orb-1 { width: 600px; height: 600px; background: radial-gradient(circle, #1a472a, transparent); top: -150px; left: -150px; animation: orbFloat 12s ease-in-out infinite; }
.orb-2 { width: 500px; height: 500px; background: radial-gradient(circle, #4ade80, transparent); bottom: 20%; right: -100px; animation: orbFloat 15s ease-in-out infinite reverse; }
.orb-3 { width: 400px; height: 400px; background: radial-gradient(circle, #bbf7d0, transparent); top: 40%; left: 40%; animation: orbFloat 18s ease-in-out infinite; }
.light-stream { position: absolute; width: 1px; background: linear-gradient(to bottom, transparent, rgba(26,71,42,0.12), transparent); animation: streamFlow 6s ease-in-out infinite; }
.stream-1 { height: 40%; top: 5%; left: 20%; }
.stream-2 { height: 30%; top: 30%; left: 75%; animation-delay: 3s; }
.geo-shape { position: absolute; opacity: 0.22; animation: geoSpin 20s linear infinite; }
.hex { width: 300px; height: 300px; border: 2px solid #4ade80; clip-path: polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%); top: 10%; right: 5%; }
.triangle { width: 200px; height: 200px; border: 2px solid #f59e0b; clip-path: polygon(50% 0%,100% 100%,0% 100%); bottom: 15%; left: 5%; animation-direction: reverse; }
.circle-ring { width: 250px; height: 250px; border: 2px solid #86efac; border-radius: 50%; top: 55%; right: 15%; animation-duration: 30s; }
@keyframes orbFloat { 0%,100%{transform:translate(0,0)} 50%{transform:translate(30px,20px)} }
@keyframes streamFlow { 0%,100%{opacity:0;transform:scaleY(.5)} 50%{opacity:1;transform:scaleY(1)} }
@keyframes geoSpin { from{transform:rotate(0)} to{transform:rotate(360deg)} }

/* ── Container ──────────────────────────────────────── */
.container-wide { max-width: 1280px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 2; }

/* ── Hero ───────────────────────────────────────────── */
.agenda-hero {
  position: relative;
  padding: 140px 0 110px;
  text-align: center;
  z-index: 2;
  background: linear-gradient(135deg, #0f2419 0%, #1a472a 60%, #2d5f3f 100%);
  overflow: hidden;
}
.hero-cursor-glow {
  position: absolute; width: 320px; height: 320px; border-radius: 50%;
  background: radial-gradient(circle, rgba(74,222,128,0.15), transparent 70%);
  transform: translate(-50%,-50%); pointer-events: none;
  transition: left 0.08s ease, top 0.08s ease; z-index: 0;
}
.hero-pill {
  position: absolute; background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.25);
  color: rgba(255,255,255,0.7); font-size: 0.78rem; font-weight: 600;
  padding: 6px 14px; border-radius: 50px; display: flex; align-items: center;
  gap: 6px; backdrop-filter: blur(6px); pointer-events: none; z-index: 1;
}
.hero-pill i { color: #4ade80; }
.pill-1 { top: 22%; left: 7%; animation: pillFloat 6s ease-in-out infinite; }
.pill-2 { top: 35%; right: 8%; animation: pillFloat 8s ease-in-out infinite reverse; }
.pill-3 { bottom: 28%; left: 5%; animation: pillFloat 7s ease-in-out infinite 1s; }
@keyframes pillFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }

.hero-badge {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(74,222,128,0.12); border: 1px solid rgba(74,222,128,0.35);
  color: #4ade80; padding: 8px 20px; border-radius: 50px;
  font-size: 0.85rem; font-weight: 600; letter-spacing: 1px;
  text-transform: uppercase; margin-bottom: 24px; backdrop-filter: blur(8px);
}
.badge-icon { font-size: 1rem; }

.hero-title {
  font-size: clamp(2.4rem, 5vw, 4rem); font-weight: 800;
  color: #ffffff; line-height: 1.1; margin-bottom: 20px;
}
.title-highlight {
  background: linear-gradient(90deg, #4ade80, #f59e0b, #4ade80);
  background-size: 200%; -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; background-clip: text;
  animation: shimmer 4s linear infinite;
}
@keyframes shimmer { 0%{background-position:0% center} 100%{background-position:200% center} }

.hero-subtitle {
  font-size: 1.1rem; color: rgba(255,255,255,0.65);
  max-width: 620px; margin: 0 auto 28px; line-height: 1.7;
}
.hero-meta { display: flex; justify-content: center; align-items: center; gap: 14px; flex-wrap: wrap; }
.meta-item { color: rgba(255,255,255,0.5); font-size: 0.9rem; display: flex; align-items: center; gap: 6px; }
.meta-item i { color: #4ade80; }
.meta-divider { color: rgba(255,255,255,0.2); }

.hero-stats {
  display: inline-flex; align-items: center; margin-top: 40px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(74,222,128,0.2);
  border-radius: 60px; padding: 16px 36px; backdrop-filter: blur(12px);
  flex-wrap: wrap; justify-content: center;
}
.stat-item { display: flex; flex-direction: column; align-items: center; padding: 0 24px; }
.stat-num { font-size: 1.8rem; font-weight: 800; color: #4ade80; line-height: 1; }
.stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 1px; margin-top: 4px; }
.stat-divider { width: 1px; height: 36px; background: rgba(74,222,128,0.2); flex-shrink: 0; }

.hero-wave { position: absolute; bottom: -1px; left: 0; right: 0; line-height: 0; }
.hero-wave svg { display: block; width: 100%; height: 90px; }

/* ── Agenda Content ─────────────────────────────────── */
.agenda-content { position: relative; z-index: 2; padding: 80px 0 100px; }

/* ── Day Tabs ───────────────────────────────────────── */
.day-tabs {
  display: flex; gap: 12px; margin-bottom: 40px;
  flex-wrap: wrap; justify-content: center;
}
.day-tab {
  display: flex; flex-direction: column; align-items: center;
  padding: 14px 28px; border-radius: 14px; border: 1.5px solid rgba(26,71,42,0.14);
  background: #ffffff; cursor: pointer; transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06); min-width: 140px;
}
.day-tab:hover { border-color: rgba(74,222,128,0.5); transform: translateY(-3px); }
.day-tab.active {
  background: linear-gradient(135deg, #1a472a, #2d5f3f);
  border-color: transparent;
  box-shadow: 0 8px 30px rgba(26,71,42,0.25);
  transform: translateY(-4px);
}
.tab-date { font-size: 0.8rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.day-tab.active .tab-date { color: rgba(255,255,255,0.6); }
.tab-title { font-size: 0.9rem; font-weight: 700; color: #0f2419; text-align: center; }
.day-tab.active .tab-title { color: #ffffff; }

/* ── Day Header Card ────────────────────────────────── */
.day-header-card {
  display: flex; align-items: center; gap: 24px;
  background: linear-gradient(135deg, #1a472a, #2d5f3f);
  border-radius: 20px; padding: 32px 36px; margin-bottom: 32px;
  box-shadow: 0 8px 40px rgba(26,71,42,0.2);
}
.day-num {
  font-size: 4rem; font-weight: 900; color: rgba(255,255,255,0.15);
  line-height: 1; flex-shrink: 0; font-variant-numeric: tabular-nums;
}
.day-info { flex: 1; }
.day-name { font-size: 1.6rem; font-weight: 800; color: #ffffff; margin-bottom: 8px; }
.day-date { font-size: 0.9rem; color: rgba(255,255,255,0.6); margin-bottom: 4px; }
.day-date i, .day-theme i { color: #4ade80; margin-right: 6px; }
.day-theme { font-size: 0.9rem; color: #4ade80; font-weight: 600; }

/* ── Sessions ───────────────────────────────────────── */
.sessions-list { display: flex; flex-direction: column; gap: 16px; margin-bottom: 60px; }

.session-card {
  display: flex; gap: 24px; background: #ffffff;
  border: 1px solid rgba(26,71,42,0.1); border-radius: 16px;
  padding: 24px 28px; box-shadow: 0 3px 20px rgba(0,0,0,0.07);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  border-left: 4px solid #e5e7eb;
}
.session-card:hover { transform: translateX(4px); box-shadow: 0 8px 30px rgba(0,0,0,0.1); }

/* Left border colors by type */
.type-keynote   { border-left-color: #4ade80; }
.type-featured  { border-left-color: #f59e0b; }
.type-panel     { border-left-color: #3b82f6; }
.type-masterclass { border-left-color: #8b5cf6; }
.type-lunch     { border-left-color: #f97316; }
.type-break     { border-left-color: #d1d5db; }
.type-registration { border-left-color: #06b6d4; }
.type-closing   { border-left-color: #1a472a; }
.type-tour      { border-left-color: #10b981; background: #f0fdf4; }

.session-time {
  flex-shrink: 0; min-width: 130px; font-size: 0.82rem; font-weight: 700;
  color: #6b7280; display: flex; align-items: flex-start; gap: 6px;
  padding-top: 3px; white-space: nowrap;
}
.session-time i { color: #4ade80; margin-top: 1px; }

.session-body { flex: 1; }
.session-top { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }

.session-badge {
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.6px; padding: 3px 10px; border-radius: 50px;
  background: rgba(26,71,42,0.08); color: #1a472a;
}
.badge-keynote     { background: rgba(74,222,128,0.15); color: #15803d; }
.badge-featured    { background: rgba(245,158,11,0.15); color: #b45309; }
.badge-panel       { background: rgba(59,130,246,0.12); color: #1d4ed8; }
.badge-masterclass { background: rgba(139,92,246,0.12); color: #6d28d9; }
.badge-lunch       { background: rgba(249,115,22,0.12); color: #c2410c; }
.badge-break       { background: rgba(209,213,219,0.5); color: #6b7280; }
.badge-registration{ background: rgba(6,182,212,0.12); color: #0e7490; }
.badge-closing     { background: rgba(26,71,42,0.12); color: #1a472a; }
.badge-tour        { background: rgba(16,185,129,0.15); color: #047857; }

.session-location { font-size: 0.78rem; color: #9ca3af; margin-left: auto; white-space: nowrap; }
.session-location i { color: #f59e0b; margin-right: 3px; }

.session-title { font-size: 1.05rem; font-weight: 700; color: #0f2419; margin-bottom: 4px; }

/* Participants tag (special design) */
.session-participants {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 12px;
  padding: 8px 8px 8px 14px;
  border-radius: 50px;
  background: linear-gradient(135deg, #0f2419, #1a472a 55%, #2d5f3f);
  border: 1px solid rgba(74, 222, 128, 0.4);
  box-shadow: 0 8px 22px rgba(26, 71, 42, 0.28);
}
.sp-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #4ade80;
  border-right: 1px solid rgba(255, 255, 255, 0.22);
  padding-right: 12px;
}
.sp-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
  padding-right: 6px;
}
.session-subtitle {
  font-size: 0.95rem; font-weight: 600; color: #1a472a;
  border-left: 3px solid #4ade80; padding-left: 10px;
  margin: 6px 0 8px; line-height: 1.5;
}
.session-desc { font-size: 0.875rem; color: #6b7280; line-height: 1.6; }
.session-items { margin: 6px 0 0; padding-left: 20px; font-size: 0.875rem; color: #6b7280; line-height: 1.7; }
.session-items li { margin-bottom: 2px; }

/* ── Transitions ────────────────────────────────────── */
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(12px); }
.fade-slide-leave-to   { opacity: 0; transform: translateY(-8px); }

/* ── CTA ────────────────────────────────────────────── */
.agenda-cta {
  text-align: center; padding: 60px 40px;
  background: linear-gradient(135deg, #0f2419 0%, #1a472a 60%, #2d5f3f 100%);
  border: 1px solid rgba(74,222,128,0.25);
  border-radius: 24px; position: relative; overflow: hidden;
  box-shadow: 0 20px 60px rgba(15,36,25,0.35);
}
.cta-glow { position: absolute; inset: 0; background: radial-gradient(ellipse at center, rgba(74,222,128,0.14), transparent 70%); pointer-events: none; }
.cta-title { font-size: 2rem; font-weight: 800; color: #ffffff; margin-bottom: 12px; }
.cta-subtitle { font-size: 1rem; color: rgba(255,255,255,0.82); max-width: 500px; margin: 0 auto 30px; }
.cta-btn {
  display: inline-flex; align-items: center; gap: 10px;
  background: linear-gradient(135deg, #1a472a, #4ade80);
  color: #ffffff; font-weight: 700; font-size: 1rem;
  padding: 14px 36px; border-radius: 50px; text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 30px rgba(74,222,128,0.3);
}
.cta-btn:hover { transform: translateY(-3px); box-shadow: 0 14px 40px rgba(74,222,128,0.5); color: #ffffff; }

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 768px) {
  .agenda-hero { padding: 120px 0 100px; }
  .hero-pill { display: none; }
  .hero-title { font-size: 2.2rem; }
  .hero-stats { padding: 12px 20px; }
  .stat-item { padding: 0 14px; }
  .stat-num { font-size: 1.4rem; }
  .day-tabs { flex-direction: column; }
  .day-tab { min-width: unset; flex-direction: row; justify-content: space-between; }
  .day-header-card { flex-direction: column; align-items: flex-start; padding: 24px; }
  .day-num { font-size: 2.5rem; }
  .session-card { flex-direction: column; gap: 10px; }
  .session-time { min-width: unset; }
  .session-location { margin-left: 0; }
  .agenda-cta { padding: 40px 24px; }
  .cta-title { font-size: 1.5rem; }
}
</style>
